/**
 * LVGL-conformant border geometry for the shared main-object rect.
 *
 * This is a pure geometry module — no Konva, no canvas state, no widget
 * knowledge — so it can be unit tested directly (see __tests__/borderGeometry).
 * CustomRect is its only renderer; every widget reaches it through the single
 * `this.rect` created in ObjectGroup, so there is no per-widget border code.
 *
 * The model is taken verbatim from LVGL 7.11's lv_draw_rect.c `draw_border()`
 * (the version openHASP vendors). LVGL does NOT stroke the four sides — it
 * fills a RING: an outer rounded-rect mask minus an inverted inner rounded-rect
 * mask, where `border_side` only changes the inner rect's insets:
 *
 *     rout = min(radius, min(w,h)/2)
 *     inner.x1 += (side & LEFT)   ?  bw : -(bw + rout)
 *     inner.x2 -= (side & RIGHT)  ?  bw : -(bw + rout)
 *     inner.y1 += (side & TOP)    ?  bw : -(bw + rout)
 *     inner.y2 -= (side & BOTTOM) ?  bw : -(bw + rout)
 *     rin  = max(0, rout - bw)
 *
 * A disabled side pushes the inner rect OUTSIDE the widget, so on that edge the
 * subtracted shape contributes no boundary at all: the neighbouring bands run
 * to the edge and taper along the outer corner arc, while the outer arcs on the
 * enabled corners survive untouched. That is exactly how a device renders
 * `border_side: 7` — top+bottom+left with no vertical right edge, but with the
 * top-right/bottom-right outer arcs tapering away. It falls out of the ring
 * model with no special-casing.
 *
 * The `top_only`/`bottom_only`/`LEFT`/`RIGHT` branches in lv_draw_rect.c
 * (lines 472-491) are pure draw-area optimisations — `corner_size` is
 * `max(rout, bw - 1)`, always >= bw, so they never clip a real pixel. They are
 * deliberately not reproduced here.
 *
 * Because the border is one FILL and not a set of strokes, partial opacity is
 * uniform: corners are painted exactly once, never doubled.
 */

export const BORDER_SIDE_NONE = 0;
export const BORDER_SIDE_BOTTOM = 1;
export const BORDER_SIDE_TOP = 2;
export const BORDER_SIDE_LEFT = 4;
export const BORDER_SIDE_RIGHT = 8;
export const BORDER_SIDE_FULL = 15;

/** Full opacity, matching LVGL's LV_OPA_COVER — the default when border_opa is unset. */
export const BORDER_OPA_COVER = 255;

export interface BorderInput {
    width: number;
    height: number;
    radius?: number | null;
    borderWidth?: number | null;
    /** LVGL bitmask; undefined/null means FULL, matching lv_draw_rect_dsc_init(). */
    borderSide?: number | null;
}

export interface BorderRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface BorderGeometry {
    /** False when nothing should be painted at all (no width, no sides, degenerate box). */
    visible: boolean;
    /** Effective outer radius — also the radius the background fill must use. */
    rout: number;
    /** Effective inner radius. */
    rin: number;
    outer: BorderRect;
    inner: BorderRect;
    /**
     * True when the inner rect has collapsed (border width >= half the box), so
     * the ring degenerates to a solid shape and the inner subpath is omitted.
     */
    solid: boolean;
}

/** Coerce a possibly-missing / string / NaN value to a finite number. */
function num(value: unknown, fallback: number): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * The effective outer radius for a box — `min(radius, w/2, h/2)`, never
 * negative. Exported because the background fill must use the exact same value
 * as the border's outer edge or the two silently drift apart.
 */
export function effectiveRadius(width: number, height: number, radius?: number | null): number {
    const w = Math.max(0, num(width, 0));
    const h = Math.max(0, num(height, 0));
    return Math.max(0, Math.min(num(radius, 0), w / 2, h / 2));
}

/**
 * Resolve the ring geometry for one main object. Pure — same inputs always
 * produce the same output, no rounding to device pixels (the stage transform
 * may place the shape on fractional coordinates at non-integer zoom).
 */
export function resolveBorderGeometry(input: BorderInput): BorderGeometry {
    const width = Math.max(0, num(input.width, 0));
    const height = Math.max(0, num(input.height, 0));
    const bw = Math.max(0, num(input.borderWidth, 0));
    const side = num(input.borderSide, BORDER_SIDE_FULL);

    const rout = effectiveRadius(width, height, input.radius);
    const rin = Math.max(0, rout - bw);
    const outer: BorderRect = { x: 0, y: 0, width, height };

    if (bw <= 0 || (side & BORDER_SIDE_FULL) === BORDER_SIDE_NONE || width <= 0 || height <= 0) {
        return { visible: false, rout, rin, outer, inner: { x: 0, y: 0, width: 0, height: 0 }, solid: false };
    }

    // A disabled side is pushed out by (bw + rout) so its corner arcs land
    // outside the widget and contribute no inner boundary — see the file header.
    const away = -(bw + rout);
    const x1 = (side & BORDER_SIDE_LEFT) ? bw : away;
    const y1 = (side & BORDER_SIDE_TOP) ? bw : away;
    const x2 = width - ((side & BORDER_SIDE_RIGHT) ? bw : away);
    const y2 = height - ((side & BORDER_SIDE_BOTTOM) ? bw : away);

    const innerWidth = x2 - x1;
    const innerHeight = y2 - y1;
    const solid = innerWidth <= 0 || innerHeight <= 0;

    return {
        visible: true,
        rout,
        rin: solid ? 0 : Math.min(rin, innerWidth / 2, innerHeight / 2),
        outer,
        inner: solid
            ? { x: 0, y: 0, width: 0, height: 0 }
            : { x: x1, y: y1, width: innerWidth, height: innerHeight },
        solid,
    };
}

/**
 * The minimal 2D-context surface these path builders need. Konva's Context
 * satisfies it, and so does a plain recording object in tests — which is how
 * the "exactly two subpaths, one even-odd fill" invariant is asserted without
 * a canvas.
 */
export interface PathSink {
    moveTo(x: number, y: number): void;
    lineTo(x: number, y: number): void;
    arc(x: number, y: number, radius: number, startAngle: number, endAngle: number, counterclockwise?: boolean): void;
    rect(x: number, y: number, width: number, height: number): void;
    closePath(): void;
}

/**
 * Append one rounded-rect subpath. Kept identical in shape to the background
 * path so the border's outer edge and the background edge coincide exactly.
 */
export function appendRoundedRect(
    sink: PathSink,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
): void {
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    if (r <= 0) {
        sink.rect(x, y, width, height);
        return;
    }
    sink.moveTo(x + r, y);
    sink.lineTo(x + width - r, y);
    sink.arc(x + width - r, y + r, r, Math.PI * 1.5, 0, false);
    sink.lineTo(x + width, y + height - r);
    sink.arc(x + width - r, y + height - r, r, 0, Math.PI * 0.5, false);
    sink.lineTo(x + r, y + height);
    sink.arc(x + r, y + height - r, r, Math.PI * 0.5, Math.PI, false);
    sink.lineTo(x, y + r);
    sink.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
}

/**
 * Append the complete ring: outer subpath, then the inner subpath as a hole.
 * The caller fills it with the even-odd rule, clipped to the outer shape —
 * the clip is required, not cosmetic, because on a disabled side the inner
 * rect extends past the outer shape and would otherwise be painted.
 *
 * Exactly one fill happens for the whole ring, which is what keeps a
 * translucent border from darkening where two sides meet.
 */
export function appendRingPath(sink: PathSink, geom: BorderGeometry): void {
    appendRoundedRect(sink, geom.outer.x, geom.outer.y, geom.outer.width, geom.outer.height, geom.rout);
    sink.closePath();
    if (geom.solid) return;
    appendRoundedRect(sink, geom.inner.x, geom.inner.y, geom.inner.width, geom.inner.height, geom.rin);
    sink.closePath();
}
